// Jedno miejsce, które odpowiada na pytanie „ile miejsca naprawdę jest".
//
// Powód istnienia tego modułu jest konkretny i kosztował wysłanie aplikacji na
// telefon w stanie awaryjnym. Powłoka stała na `min-height: 100vh`, a pomiar
// porównywał `document.documentElement.scrollHeight` z `window.innerHeight`.
// Na iOS to są DWA RÓŻNE viewporty: jednostka `vh` liczy się od DUŻEGO (pasek
// adresu schowany), `innerHeight` od MAŁEGO (pasek widoczny). Dokument był
// więc z definicji wyższy niż okno, każdy z ośmiu wariantów oblewał pomiar,
// a `fitLoop` schodził do trybu awaryjnego z pismem podłogowym.
//
// W headless WebKicie na Linuksie obie liczby są równe, więc błąd był tam
// NIEWIDZIALNY: dziewięć zielonych testów e2e i sprawdzenie żywej strony
// pokazywały „ok" w chwili, gdy telefon pokazywał stan awaryjny. Stąd wniosek
// mocniejszy niż sama łatka: aplikacja i harness testowy muszą liczyć
// dostępną wysokość TYM SAMYM kodem, bo rozjazd między nimi jest właśnie tym,
// co ukryło błąd.

/** Nazwa własności CSS, z której powłoka bierze wysokość pudełka aplikacji. */
export const ZMIENNA_WYSOKOSCI = '--zar-h';

/** Wysokość naprawdę widoczna. `visualViewport` uwzględnia paski przeglądarki
 * i klawiaturę, `innerHeight` jest zapasem dla środowisk bez tego API. */
export function dostepnaWysokosc(): number {
  return Math.round(window.visualViewport?.height ?? window.innerHeight);
}

/** Zapisuje dostępną wysokość do CSS. Jedyne wejście układu na temat rozmiaru
 * ekranu: powłoka nie używa `vh`, `dvh` ani `innerHeight` nigdzie indziej. */
export function ustawWysokosc(): void {
  document.documentElement.style.setProperty(ZMIENNA_WYSOKOSCI, `${dostepnaWysokosc()}px`);
}

/** Podpina przeliczenie do wszystkich zdarzeń, które zmieniają dostępne pole.
 * `scroll` na `visualViewport` jest tu równie ważny jak `resize`: na iOS to
 * właśnie on melduje zwinięcie się paska adresu. Zwraca funkcję odpinającą. */
export function sledzWysokosc(przelicz: () => void): () => void {
  // Bramka na ZMIANIE wysokości, nie na samym zdarzeniu. Na iOS `scroll`
  // z `visualViewport` sypie się wielokrotnie, gdy pasek adresu się układa,
  // a każde przeliczenie to kompilacja wariantów i kilkanaście pomiarów,
  // z których każdy wymusza synchroniczny layout. Bez bramki ruch paska
  // kosztuje kilkadziesiąt pełnych dopasowań, a wynik jest ten sam.
  let ostatnia = dostepnaWysokosc();
  const uchwyt = (): void => {
    const teraz = dostepnaWysokosc();
    if (teraz === ostatnia) return;
    ostatnia = teraz;
    ustawWysokosc();
    przelicz();
  };
  window.addEventListener('resize', uchwyt);
  window.visualViewport?.addEventListener('resize', uchwyt);
  window.visualViewport?.addEventListener('scroll', uchwyt);
  return () => {
    window.removeEventListener('resize', uchwyt);
    window.visualViewport?.removeEventListener('resize', uchwyt);
    window.visualViewport?.removeEventListener('scroll', uchwyt);
  };
}

/**
 * Czy treść przelewa się poza własne pudełko elementu.
 *
 * Porównanie `scrollHeight` z `clientHeight` TEGO SAMEGO elementu nie zależy
 * ani od jednostek viewportu, ani od tego, czy pasek przeglądarki jest akurat
 * widoczny, więc nie da się przez nie powtórzyć błędu opisanego wyżej.
 * Warunkiem koniecznym jest definitywna wysokość pudełka, którą daje powłoka
 * (`#app { height: var(--zar-h) }` plus `#tresc { min-height: 0 }`).
 */
export function miesciSie(kontener: HTMLElement): boolean {
  return miesciSieNaWysokosc(kontener)
    && nadmiarSzerokosci(kontener) <= TOLERANCJA_SZEROKOSCI_PX;
}

/** Sam wymiar pionowy. Osobno, bo przebieg łagodny dopasowania (patrz
 * `dopasuj.ts`) świadomie DOPUSZCZA zawinięcie wiersza akordów na granicy
 * frazy, więc nadmiar w poziomie nie jest wtedy błędem, tylko zamierzonym
 * ustępstwem na rzecz kotwic tekstowych. */
export function miesciSieNaWysokosc(kontener: HTMLElement): boolean {
  return nadmiarWysokosci(kontener) <= TOLERANCJA_PX;
}

/** Pół piksela na zaokrąglenia układu subpikselowego. Powyżej tego treść
 * NAPRAWDĘ wychodzi poza pole, a nie „prawie się mieści". Eksportowana, bo
 * test wysokości musi stawiać DOKŁADNIE ten sam próg co pomiar produkcyjny:
 * przy własnym, ostrzejszym progu oblewałby dopasowania, które produkcja
 * świadomie przyjmuje, i mierzyłby test zamiast aplikacji. */
export const TOLERANCJA_PX = 0.5;

/**
 * O ile pikseli treść wychodzi poza pudełko kontenera, licząc w OBIE strony.
 *
 * Samo `scrollHeight <= clientHeight` tu nie wystarcza i to też jest wniosek
 * z pomiaru, nie ostrożność. W kontenerze fleksowym z `min-height: 0`
 * elementy, które nie chcą się skurczyć, wylewają się poza pudełko, a
 * `scrollHeight` potrafi tego nie odnotować: zmierzone na `nie-placz-ewka.txt`
 * przy poziomie 0, gdzie ostatni wiersz akordów wchodził na pasek układu, a
 * `scrollHeight` i `clientHeight` zgodnie pokazywały 781. Nadmiar NAD górną
 * krawędzią `scrollHeight` pomija zresztą z definicji.
 *
 * Porównanie prostokątów nie ma jak skłamać, ale musi być względem WŁASNEGO
 * RODZICA każdego elementu, a nie względem kontenera, i to jest poprawka po
 * zrzucie z telefonu MH. Wcześniejsza wersja pytała tylko o krawędź
 * kontenera, więc przepuszczała dokładnie ten przypadek, który tam widać:
 * sekcje wylewały się z `.og-body` na pasek mapy układu, ale mapa jest
 * OSTATNIM dzieckiem kontenera, więc nadmiar zjadał jej wysokość i nie
 * sięgał dolnej krawędzi kontenera. Pomiar odpowiadał „mieści się", a tekst
 * nachodził na tekst. Sprawdzanie każdego elementu wobec jego rodzica łapie
 * przelanie na każdym poziomie zagnieżdżenia, nie tylko na ostatnim.
 *
 * Kilkadziesiąt wywołań `getBoundingClientRect` na pomiar to koszt bez
 * znaczenia wobec tego, że pomiar i tak wymusza synchroniczne przeliczenie
 * układu. Prostokąty zbieramy raz do mapy, żeby nie czytać ich dwa razy.
 */
export function nadmiarWysokosci(kontener: HTMLElement): number {
  const prostokat = new Map<Element, DOMRect>();
  prostokat.set(kontener, kontener.getBoundingClientRect());
  for (const element of kontener.querySelectorAll('*')) {
    prostokat.set(element, element.getBoundingClientRect());
  }

  let nadmiar = kontener.scrollHeight - kontener.clientHeight;
  for (const [element, r] of prostokat) {
    if (element === kontener) continue;
    if (r.width === 0 && r.height === 0) continue;
    // Elementy LINIOWE są wyłączone i to jest poprawka po drugim zrzucie z
    // telefonu MH, zmierzona, nie zgadnięta. Prostokąt elementu liniowego
    // liczy się z metryk kroju (górna plus dolna wysokość znaku), a nie z
    // `line-height` wiersza, w którym stoi. Na Linuksie zamienniki krojów mają
    // ten stosunek równy 1,15 przy `line-height: 1,16`, czyli mieszczą się o
    // włos. Kroje systemowe iOS mają go w okolicach 1,3, więc KAŻDY element
    // liniowy zgłaszał tam przelanie o kilka pikseli, każdy wariant oblewał
    // pomiar i skrót zjeżdżał na poziom 7 z pismem podłogowym: scalone sekcje,
    // zero kotwic, dziewięć dziesiątych ekranu puste.
    //
    // Wyłączenie niczego nie chowa: wiersz, w którym stoi element liniowy, jest
    // elementem blokowym i dalej podlega sprawdzeniu, a układ pionowy zależy od
    // wysokości WIERSZY, nie od wysokości prostokątów znaków w nich.
    if (getComputedStyle(element).display === 'inline') continue;
    const rodzic = prostokat.get(element.parentElement as Element);
    if (rodzic === undefined) continue;
    nadmiar = Math.max(nadmiar, r.bottom - rodzic.bottom, rodzic.top - r.top);
  }
  return nadmiar;
}

/** Jeden piksel na ogon odstępu międzyliterowego. `letter-spacing` dokłada się
 * także PO ostatnim znaku wiersza, więc `scrollWidth` bywa większy od
 * `clientWidth` o ułamek znaku przy tekście, który mieści się bez reszty. */
export const TOLERANCJA_SZEROKOSCI_PX = 1;

/**
 * O ile pikseli treść wychodzi poza pudełko W POZIOMIE.
 *
 * Osobna funkcja, bo wykrywa się to inaczej niż nadmiar w pionie. Wiersz
 * akordów ma `white-space: nowrap`, więc tekst przelewa się poza blok, ale
 * PROSTOKĄT samego bloku zostaje w kontenerze i porównanie prostokątów niczego
 * nie widzi. Widzi to dopiero `scrollWidth` względem `clientWidth` na tym
 * samym elemencie.
 *
 * Po co to w ogóle: bez wymiaru poziomego dopasowanie pilnowało wyłącznie
 * wysokości, więc wiersz akordów zawijał się w połowie frazy i drugą linijkę
 * zaczynał sierotą, jak `Am Em Am` na zrzucie z telefonu. Poziom 2 degradacji
 * ma z definicji dawać JEDEN wiersz na sekcję, więc zawijanie było cichym
 * złamaniem tego, co wariant obiecuje.
 */
export function nadmiarSzerokosci(kontener: HTMLElement): number {
  let nadmiar = kontener.scrollWidth - kontener.clientWidth;
  for (const element of kontener.querySelectorAll('*')) {
    nadmiar = Math.max(nadmiar, element.scrollWidth - element.clientWidth);
  }
  return nadmiar;
}
